import { ArrowDown, FileText, Paperclip, Send, X } from 'lucide-react'
import { Fragment, useEffect, useLayoutEffect, useRef, useState } from 'react'

import { Avatar } from '@/components/ui/Avatar'
import { Skeleton } from '@/components/feedback/Skeleton'
import { cn } from '@/lib/cn'
import { useFormat } from '@/i18n/useFormat'
import {
  formatBytes,
  renderableKind,
} from '@/features/cases/lib/attachmentKind'
import type { ChatAttachment, ChatMessage } from '@/types/domain'
import { useTranslation } from 'react-i18next'

/* A record's conversation (Story 8.3, FR-46, UX-DR-13, AD-22).
 *
 * This component is the conversation and nothing else — the thread, the paging
 * control, the jump-to-latest affordance and the composer. WHERE it sits is the
 * caller's decision, and there are exactly two callers:
 *
 *   · `features/chat/components/ChatDock` — the record dock, which since 0052
 *     mounts it as a FLOATING panel anchored bottom-right on desktop (with a
 *     collapsed state that leaves a button carrying the unread count) and as a
 *     full-screen view on a phone, for a case AND for a quote. Those two shapes
 *     are the same mount, so parity is structural rather than a discipline
 *     somebody has to maintain (UX-DR-28).
 *   · `routes/ChatPage` — the cross-record chat page, beside its conversation
 *     list.
 *
 * Both get the SAME panel, which is the point: a thread read from the chat page
 * and the same thread read from its record cannot drift, because there is no
 * second implementation for one of them to drift into. Nothing in this file
 * knows the subject kind, which is what let one panel serve both records
 * without a second component.
 *
 * What this file does NOT draw is the internal-only notice — both callers open
 * with their own banner and an `Internal` chip, and the strip that used to live
 * here made every surface say it twice. See the note where it stood.
 *
 * It is not a tab, and that remains load-bearing. The D1 incident in the client
 * questionnaire was caused by local operations failing to tell the truck manager
 * and the overseas operationist that a reloading plan had changed; a tab buries
 * that conversation inside one section of one record.
 *
 * Internal only, always — external participants are structurally absent (FR-46).
 */

/* One attachment inside a sent bubble.
 *
 * What it renders is decided by renderableKind() against a closed allowlist,
 * never by the content type directly — see that module for why. Anything not
 * explicitly allowed lands as the file row at the bottom, which is a link and
 * not a rendered document.
 *
 * `src` is absent for anything already sent, because a stored attachment needs
 * a freshly signed URL per read (AD-18) and that endpoint arrives with the real
 * chat module. Until then only the local preview of a just-picked file has one,
 * so a sent photo shows its file row — honest about what it can currently do,
 * rather than an <img> pointed at nothing.
 */
function SentAttachment({
  attachment,
  own,
}: {
  attachment: ChatAttachment
  own: boolean
}) {
  const kind = renderableKind(attachment)
  const src = attachment.previewUrl

  // A media element with no source would render as a broken frame; the file
  // row is the honest fallback and is also what every non-media type gets.
  if (src && kind === 'image') {
    return (
      <img
        src={src}
        alt={attachment.fileName}
        className="max-h-64 w-full rounded-xl object-cover"
      />
    )
  }

  if (src && kind === 'video') {
    return (
      <video
        src={src}
        controls
        preload="metadata"
        className="max-h-64 w-full rounded-xl"
      />
    )
  }

  if (src && kind === 'audio') {
    // Audio gets the filename above the transport: a voice note and a recorded
    // call look identical as a bare player, and which one it is matters.
    return (
      <div className="flex flex-col gap-1">
        <span className="truncate text-[12px] opacity-80">{attachment.fileName}</span>
        <audio src={src} controls preload="metadata" className="w-full" />
      </div>
    )
  }

  return (
    <div
      className={cn(
        'flex items-center gap-2 rounded-xl px-2.5 py-2',
        own ? 'bg-white/15' : 'bg-surface-sunken border-line border',
      )}
    >
      <FileText size={15} aria-hidden className="shrink-0 opacity-70" />
      <span className="min-w-0 flex-1 truncate text-[12.5px]">
        {attachment.fileName}
      </span>
      <span className="shrink-0 text-[11px] opacity-70">
        {formatBytes(attachment.byteSize)}
      </span>
    </div>
  )
}

export interface ChatPanelProps {
  messages: ChatMessage[]
  currentUserId: string
  /** The signed-in user's photo (FR-58, AD-18), absent when they have none.
   *
   *  Passed in rather than read from the session here, because a panel that
   *  fetches its own viewer is a second source of truth for who is signed in —
   *  and this one already takes currentUserId from the same place. It wins over
   *  a message's own authorAvatarUrl on own messages: the session link is
   *  re-minted on every read, so it is the one that has not expired. */
  currentUserAvatarUrl?: string
  /** Sends the message. `files` is present only when something was attached,
   *  so a text-only send is byte-identical to what it was before. */
  onSend: (draft: { body: string; files?: File[] }) => void
  /** Whether this caller can actually store an attachment.
   *
   *  OFF by default, and that default is the honest one. The real chat schema
   *  (migration 0023) has no attachment table: a message cannot reference a
   *  file, so a composer that offered a paperclip here would upload bytes the
   *  message could never point at — a photo that appears to send and is then
   *  unreachable forever. Better to not offer it than to drop it silently.
   *
   *  Kept as a flag rather than deleting the attachment code, because the
   *  drag-and-drop, the size refusal and the type allowlist are all proven by
   *  tests and are what the surface will need the day attachments are added to
   *  the schema. Files on a case have a surface that DOES persist them today
   *  (Story 8.1, FR-45) — the Files section. */
  allowAttachments?: boolean
  /** The first page is still in flight. Distinct from an empty conversation:
   *  "nobody has said anything" and "we have not looked yet" are different
   *  facts, and a panel that renders the first as the second is lying about a
   *  case somebody is about to act on. */
  isPending?: boolean
  isError?: boolean
  /** Older history exists beyond what is held (AD-22). The panel pages rather
   *  than fetching the whole conversation with the case. */
  hasMore?: boolean
  isLoadingMore?: boolean
  onLoadMore?: () => void
  className?: string
}

/** What one attachment may weigh, mirroring storage.MaxUploadBytes (50 MiB).
 *  Checked here so an oversized file is refused at the moment it is picked
 *  rather than after it has been uploaded and rejected. */
const MAX_ATTACHMENT_BYTES = 50 << 20

/** How many files may ride one message. Not a storage limit — a legibility
 *  one: past this the tray is taller than the conversation behind it. */
const MAX_ATTACHMENTS = 10

/** How close to the end counts as "at the end".
 *
 *  Not zero. A thread whose last bubble is two pixels from the bottom edge —
 *  which is what a fractional scroll height produces on a scaled display — is
 *  one the reader is plainly at the end of, and treating it as "scrolled up"
 *  would leave a jump-to-latest pill hovering over a conversation already
 *  showing its own last line. */
const AT_BOTTOM_SLACK = 40

/** A `Date`'s calendar day IN THE VIEWER'S ZONE, as the date-only string the
 *  formatters in lib/format take (`YYYY-MM-DD`).
 *
 *  The getters used here are the LOCAL ones deliberately. A message stored at
 *  23:00Z is the next day in Ashgabat (+05) and the same day in Yiwu is later
 *  still (AD-19, AD-27): grouping on the UTC date would file a Thursday
 *  afternoon instruction under Wednesday for the office that sent it, which is
 *  precisely the arithmetic a day separator exists to remove.
 *
 *  A string rather than a Date, for two jobs at once. It is the GROUPING KEY —
 *  comparing two of them is a string compare with no zone left in it to get
 *  wrong — and it is the exact shape `formatDate` parses: that function reads
 *  date-only values as UTC and formats them as UTC, so the local day computed
 *  here survives the round trip instead of being shifted back west. */
function dayKeyOf(at: Date): string {
  const month = `${at.getMonth() + 1}`.padStart(2, '0')
  const day = `${at.getDate()}`.padStart(2, '0')
  return `${at.getFullYear()}-${month}-${day}`
}

/** The same, for a wire instant. Empty for anything unparseable, which the
 *  render treats as "no separator" rather than as a day of its own — an
 *  optimistic bubble whose clock misfired must not open a section headed by
 *  an em dash. */
function dayKey(value: string | null | undefined): string {
  if (!value) return ''
  const at = new Date(value)
  return Number.isFinite(at.getTime()) ? dayKeyOf(at) : ''
}

/** The chat's three time readings, in the reader's language.
 *
 *  Through `useFormat()`, which needs no session: the locale comes from
 *  i18next, so a presentational panel renders a timestamp without a
 *  QueryClient. (This hook used to read i18next itself, because `useFormat`
 *  once reached the locale through `useSession`.)
 */
function useChatTime() {
  const { t } = useTranslation()
  const format = useFormat()

  return {
    /** `now`, `12 min`, `4 h`, then an absolute date past a week. The clock is
     *  read per call, inside `format.relative`. */
    relative: (value: string) => format.relative(value),
    /** The exact instant, for the title — one hover from the relative age. */
    exact: (value: string) => format.timestamp(value),
    /** What the separator above the first message of a day SAYS: `Today`,
     *  `Yesterday`, then a date.
     *
     *  `new Date()` is read inside the closure, per call, exactly as `relative`
     *  above does — not captured once when the hook runs. Nothing here is
     *  memoised, and that is the point: a panel left open in a warehouse over
     *  midnight would otherwise keep the last day of the shipment headed
     *  "Today", which is the one lie a date separator must not tell. The cost
     *  is a `Date` per row per render, which is nothing beside the `Intl`
     *  formatter each timestamp already builds.
     *
     *  Yesterday is reached with `setDate(-1)` rather than by subtracting
     *  86 400 000 ms: on the two days a year a zone shifts, the arithmetic
     *  answers the wrong calendar day and `setDate` answers the right one.
     *  Month and year rollover come free with it. */
    day: (value: string) => {
      const key = dayKey(value)
      if (!key) return ''

      const now = new Date()
      if (key === dayKeyOf(now)) return t('cases.chat.today')

      const yesterday = new Date(now)
      yesterday.setDate(now.getDate() - 1)
      if (key === dayKeyOf(yesterday)) return t('cases.chat.yesterday')

      /* `formatDate` and not `formatTimestamp`: this heads a WHOLE DAY, and a
         clock time in that position names one message rather than the section
         under it. The value handed over is already date-only, which is the
         only shape that function parses. */
      return format.date(key)
    },
  }
}

export function ChatPanel({
  messages,
  currentUserId,
  currentUserAvatarUrl,
  onSend,
  allowAttachments = false,
  isPending = false,
  isError = false,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore,
  className,
}: ChatPanelProps) {
  const { t } = useTranslation()
  const time = useChatTime()
  const [draft, setDraft] = useState('')
  const [atBottom, setAtBottom] = useState(true)
  const scrollRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const pickerRef = useRef<HTMLInputElement>(null)
  const count = messages.length

  /* The READ WATERMARK: how many messages the reader had when they were last
   * at the end of the thread.
   *
   * A watermark rather than an unread TALLY, and the difference is what keeps
   * this correct. A tally has to be incremented from an effect that watches the
   * length — which means state written during render-commit, cascading renders,
   * and a value that drifts the moment two updates coalesce. A watermark is
   * only ever moved by something the READER did (arriving at the end, sending,
   * pressing the pill), so `unread` below is DERIVED and cannot disagree with
   * the list it describes.
   *
   * It is state rather than a ref because the pill's label must re-render when
   * it moves; a ref would leave "3 new messages" on screen after the reader had
   * already scrolled down to them. */
  const [readAt, setReadAt] = useState(count)

  /* What the pill announces (FR-46): messages that landed below a reader who is
   * scrolled up. Derived, never stored — see above.
   *
   * Clamped at zero because the watermark can legitimately sit ABOVE the count
   * for one render: a rolled-back optimistic bubble shortens the list, and a
   * sender must not be told their own failed send was "-1 new messages".
   *
   * Zero while at the bottom, unconditionally: whatever is on screen in front
   * of somebody is by definition not news to them, and this is what makes
   * scrolling back down clear the notice without an effect to do it. */
  const unread = atBottom ? 0 : Math.max(0, count - readAt)

  /* The scroll position to restore after an older page is prepended.
   *
   * Prepending rows moves everything below them DOWN by the height of what was
   * added, so a reader who pressed "load earlier" would be thrown forward into
   * the messages they had just read. Capturing scrollHeight before the paint
   * and adding the difference back afterwards is what pins the message they
   * were looking at to the same pixel — the one piece of scroll bookkeeping in
   * this panel that has to run synchronously, hence useLayoutEffect below. */
  const anchor = useRef<number | null>(null)

  /* Whether the growth React is about to report was a PREPEND.
   *
   * Set by the layout effect that restores the scroll position, and read by the
   * arrival effect below. The two cannot share `anchor` alone, and the ordering
   * is why: useLayoutEffect runs BEFORE useEffect, so by the time the arrival
   * effect asks "was this history or an arrival?" the anchor has already been
   * consumed and cleared — and every page-back was counted as unread messages.
   * That is a bug this file had until a test found it. */
  const prepended = useRef(false)

  /* The length at the previous commit, so a prepend can move the watermark by
     exactly the number of rows that landed above the reader. A ref rather than
     state: nothing renders from it, and as state it would be the second half of
     a render loop with `readAt`. */
  const lastCount = useRef(count)

  /* Files chosen but not yet sent. Held as the browser's own File objects
     rather than uploaded on pick: an attachment is not committed until the
     message is, so removing one from the tray costs nothing and leaves no
     orphaned object in the bucket. */
  const [picked, setPicked] = useState<File[]>([])
  const [rejected, setRejected] = useState<string | null>(null)

  const [dragging, setDragging] = useState(false)
  // Depth rather than a boolean — see the handlers below for why.
  const dragDepth = useRef(0)

  // Either half is enough to send: a photo with no caption is a message.
  const canSend = draft.trim().length > 0 || picked.length > 0

  const addFiles = (selected: FileList | null) => {
    // The one choke point. Guarding HERE rather than at each of the four entry
    // paths (picker, drop, paste, and the tray) means a future fifth way in
    // cannot bypass it — which is how the drop handler ends up accepting what
    // the button refuses.
    if (!allowAttachments) return
    if (!selected?.length) return

    const incoming = Array.from(selected)
    const tooBig = incoming.find((file) => file.size > MAX_ATTACHMENT_BYTES)
    if (tooBig) {
      // Named rather than counted: with eight files selected, "one is too
      // large" leaves the user to find which.
      setRejected(t('cases.chat.tooLarge', { name: tooBig.name }))
      return
    }

    setPicked((current) => {
      const room = MAX_ATTACHMENTS - current.length
      if (room <= 0) {
        setRejected(t('cases.chat.tooMany', { count: MAX_ATTACHMENTS }))
        return current
      }
      if (incoming.length > room) {
        setRejected(t('cases.chat.tooMany', { count: MAX_ATTACHMENTS }))
      } else {
        setRejected(null)
      }
      return [...current, ...incoming.slice(0, room)]
    })
  }

  /* Restores the reading position after an older page has been prepended.
   *
   * useLayoutEffect rather than useEffect: this must run BEFORE the browser
   * paints. In a passive effect the reader would see one frame of the thread
   * jumped forward and then a frame of it corrected, which reads as the panel
   * flinching every time somebody asks for history. */
  useLayoutEffect(() => {
    if (anchor.current === null) return
    const el = scrollRef.current
    // Flagged before the early return on a missing container, so a prepend into
    // an unmeasurable thread is still not counted as an arrival.
    prepended.current = true
    if (el) el.scrollTop += el.scrollHeight - anchor.current
    anchor.current = null
  }, [count])

  /* New messages while the reader is SCROLLED UP must never move the view.
   *
   * The rule the AC states, and the one every chat gets wrong: a thread that
   * auto-scrolls on arrival takes the sentence somebody is halfway through and
   * replaces it with a sentence they have not asked for. So the scroll only
   * follows the end when the reader is ALREADY at the end; otherwise the
   * arrival is counted into the pill and the scroll position is left exactly
   * where it was (UX-DR-13).
   *
   * The count is trusted rather than the ids compared, because the only thing
   * that shortens this list is a rolled-back optimistic bubble — and a reader
   * scrolled up should not be told their own failed send was "1 new message".
   */
  useEffect(() => {
    const wasPrepend = prepended.current
    prepended.current = false

    // A prepend must not scroll to the end, and must not move the watermark:
    // the reader asked for history, and it landed ABOVE them. Counting it as
    // read would also mean counting it as unread a moment earlier, which is how
    // a page-back announces itself as "30 new messages".
    if (wasPrepend) {
      setReadAt((held) => held + (count - lastCount.current))
      lastCount.current = count
      return
    }
    lastCount.current = count

    // Only when the reader is already at the end does the view follow it. This
    // is the whole rule: anywhere else, the scroll position is left exactly
    // where it was and the arrival is announced by the derived pill instead.
    // The thread's OWN scroll container, never `scrollIntoView`: that scrolls
    // every scrollable ancestor, and while the page's entrance transform makes
    // the case page the containing block of the dock's fixed layer, the shell's
    // `<main>` is one of them — a message landing as the page opened dragged it
    // off the block a back link had just revealed (S-11).
    if (atBottom) {
      const el = scrollRef.current
      if (el) el.scrollTop = el.scrollHeight
      setReadAt(count)
    }
  }, [count, atBottom])

  const jumpToLatest = () => {
    setAtBottom(true)
    setReadAt(count)
    const el = scrollRef.current
    if (el && typeof el.scrollTo === 'function') {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })
    } else if (el) {
      el.scrollTop = el.scrollHeight
    }
  }

  const loadEarlier = () => {
    // Captured BEFORE the request rather than in the effect that runs after the
    // rows land, because by then the height has already changed and the
    // difference is unrecoverable.
    anchor.current = scrollRef.current?.scrollHeight ?? null
    onLoadMore?.()
  }

  // The composer grows with the draft instead of scrolling inside a fixed
  // two-line box. Operations paste whole agent messages in here — a taller
  // field costs a few pixels of history, a scrolling one costs the writer
  // sight of their own sentence. Height is reset to `auto` first so the
  // measurement shrinks as well as grows; the cap lives in CSS (max-h) and
  // scrollHeight respects it, so a very long draft scrolls only past that.
  useLayoutEffect(() => {
    const el = inputRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }, [draft])

  const submit = () => {
    const body = draft.trim()
    if (!body && picked.length === 0) return

    onSend(picked.length ? { body, files: picked } : { body })
    setDraft('')
    setPicked([])
    setRejected(null)
    // Sending is the one arrival that MAY move the view, and must: the sender
    // is looking at the bubble they just produced, so a message that appeared
    // below the fold would read as a send that did not happen. The unread
    // notice is cleared with it — their own message is not news to them.
    setAtBottom(true)
    // The watermark moves past the bubble this send is about to add, so a
    // sender never sees their own message counted as news.
    setReadAt(count + 1)
    inputRef.current?.focus()
  }

  return (
    /* The surrounding Drawer owns the title, the close control and the
       dismissal; this is the conversation itself and nothing else. The
       internal-only reminder stays HERE rather than in the drawer title,
       because it qualifies the messages, not the panel (FR-46). */
    <div
      aria-label={t('cases.chat.label')}
      /* Drop anywhere on the panel, not only on the composer. Someone dragging
         a photo in aims at the conversation, and a target that is only the
         28px-tall input bar is a target most drops miss.

         dragOver must preventDefault or the browser navigates to the file and
         the case is gone. The counter guards against the flicker every naive
         implementation has: dragging over a child fires dragLeave on the
         parent, so tracking depth rather than a boolean is what keeps the
         overlay from strobing as the pointer crosses each bubble. */
      onDragOver={(event) => {
        if (event.dataTransfer.types.includes('Files')) event.preventDefault()
      }}
      onDragEnter={(event) => {
        if (!event.dataTransfer.types.includes('Files')) return
        event.preventDefault()
        dragDepth.current += 1
        setDragging(true)
      }}
      onDragLeave={() => {
        dragDepth.current = Math.max(0, dragDepth.current - 1)
        if (dragDepth.current === 0) setDragging(false)
      }}
      onDrop={(event) => {
        if (!event.dataTransfer.types.includes('Files')) return
        event.preventDefault()
        dragDepth.current = 0
        setDragging(false)
        addFiles(event.dataTransfer.files)
      }}
      className={cn('bg-surface-sunken relative flex h-full flex-col', className)}
    >
      {/* Shown only while a file is actually overhead, so it costs nothing the
          rest of the time. pointer-events-none is load-bearing: an overlay that
          takes the pointer would intercept the drop it is advertising. */}
      {dragging && allowAttachments ? (
        <div className="border-brand bg-brand-soft/80 pointer-events-none absolute inset-2 z-20 flex items-center justify-center rounded-lg border-2 border-dashed backdrop-blur-[2px]">
          <p className="text-brand flex items-center gap-2 text-[13px] font-medium">
            <Paperclip size={15} aria-hidden />
            {t('cases.chat.dropHere')}
          </p>
        </div>
      ) : null}

      {/* The internal-only reminder USED TO LIVE HERE, as a dotted line of
          chrome above the thread, and it has moved OUT to the two callers
          (0052).

          Not deleted — said once instead of twice. Both surfaces that mount
          this panel now open with a banner making the same promise in more
          words: the chat page's `ChatThreadHeader` ("Internal conversation —
          visible only to your team") and the record dock's header ("Visible
          only to your team"), each paired with an `Internal` chip beside the
          title. Leaving this strip in place stacked two notices about one fact
          directly on top of each other, which is how a reader learns to stop
          reading both.

          It belongs to the CALLER rather than to the panel because the caller
          is what has a header to hang it under; a panel that drew its own
          would have to know whether the thing above it had already said so.
          `cases.chat.internalOnly` is deliberately kept in the catalogues —
          the shorter wording is what a third surface would want if one ever
          mounts this panel without a header of its own. */}

      <div
        ref={scrollRef}
        onScroll={(event) => {
          const el = event.currentTarget
          setAtBottom(
            el.scrollHeight - el.scrollTop - el.clientHeight < AT_BOTTOM_SLACK,
          )
        }}
        className="asa-scroll relative flex-1 overflow-y-auto px-4 pt-1 pb-3"
      >
        {/* Paging back through history (AD-22). A BUTTON rather than an
            infinite scroll that fires on reaching the top: the top of a chat
            panel is where a reader arrives by accident, and a silent fetch
            there both surprises them and makes the list grow under their
            scrollbar. Pressing for more is a decision, and its cost is visible.

            The one-time skeleton below is a different state — that is the FIRST
            page, which nobody asked for and which must not read as an empty
            conversation on a case that has one. */}
        {isPending ? (
          <div
            className="flex flex-col gap-3 py-2"
            aria-busy="true"
            aria-label={t('cases.chat.loading')}
          >
            <Skeleton className="h-12 w-3/4 rounded-2xl" />
            <Skeleton className="ml-auto h-10 w-2/3 rounded-2xl" />
            <Skeleton className="h-14 w-4/5 rounded-2xl" />
          </div>
        ) : null}

        {isError && count === 0 ? (
          <p role="status" className="text-negative py-4 text-center text-[12.5px]">
            {t('cases.chat.failed')}
          </p>
        ) : null}

        {hasMore && !isPending ? (
          <div className="flex justify-center pt-1 pb-2">
            <button
              type="button"
              onClick={loadEarlier}
              disabled={isLoadingMore}
              className={cn(
                'border-line bg-surface text-text-muted hover:text-text-heading',
                'hover:bg-surface-hover cursor-pointer rounded-full border px-3 py-1',
                'text-[11.5px] font-normal disabled:cursor-wait',
                'transition-colors duration-[var(--dur-fast)] ease-[var(--ease-standard)]',
              )}
            >
              {isLoadingMore ? t('cases.chat.loadingEarlier') : t('cases.chat.earlier')}
            </button>
          </div>
        ) : null}

        {!isPending && !isError && count === 0 ? (
          <p className="text-text-muted py-6 text-center text-[12.5px] font-light">
            {t('cases.chat.empty')}
          </p>
        ) : null}

        <ul className="flex flex-col gap-0.5">
          {messages.map((message, index) => {
            const own = message.authorId === currentUserId
            const previous = messages[index - 1]

            /* The first message of a calendar day, in the READER's zone. The
               very first message in the thread always is one — a conversation
               that opened with no date above it would leave the oldest thing on
               screen as the only undated line in the panel. */
            const startsDay = dayKey(message.sentAt) !== dayKey(previous?.sentAt)
            const dayLabel = startsDay ? time.day(message.sentAt) : ''

            // Consecutive messages from one author collapse into a run: the
            // avatar and the name line are drawn once, and the follow-ups sit
            // directly beneath. A fast exchange between two operationists was
            // previously three-quarters repeated identity — this is what makes
            // the thread scan as conversation rather than as a log.
            //
            // A DAY BOUNDARY breaks the run even when the same person spoke on
            // both sides of it. Without that, the first bubble under "Today"
            // would be a naked rectangle whose author and age were stated
            // yesterday, several separators up — which is the state the reader
            // opened the panel to get out of.
            const startsRun =
              !previous || previous.authorId !== message.authorId || startsDay

            return (
              <Fragment key={message.id}>
                {/* `role="presentation"` rather than a plain <li>.

                    It has to BE an <li> — a <ul> with a non-<li> child is
                    invalid, and the browser's own fixup for that is worse than
                    the markup. But it is not one of the things the list counts:
                    a screen reader announcing "list, eleven items" for eight
                    messages and three dates has been given an inventory nobody
                    can act on. Stripping the element's own role leaves its text
                    in the accessibility tree as the static heading it is, read
                    in place between the messages it divides.

                    The rules are aria-hidden because they are the label drawn
                    twice — "Today" is the whole content, and two announced
                    separators around it are noise. */}
                {dayLabel ? (
                  <li
                    role="presentation"
                    className={cn(
                      'flex items-center gap-3 px-1 pb-2 select-none',
                      // No lead-in above the very first one: it sits directly
                      // under the paging control, which brings its own space.
                      index > 0 && 'pt-4',
                    )}
                  >
                    <span aria-hidden className="border-line flex-1 border-t" />
                    <span className="text-text-muted shrink-0 text-[11px] font-normal">
                      {dayLabel}
                    </span>
                    <span aria-hidden className="border-line flex-1 border-t" />
                  </li>
                ) : null}

                <li
                  className={cn(
                    'flex items-end gap-2',
                    // The run's own lead-in, suppressed when a separator is
                    // already holding that space open above this message —
                    // otherwise a new day pays for the gap twice.
                    startsRun && index > 0 && !dayLabel && 'mt-3',
                    own && 'flex-row-reverse',
                  )}
                >
                  {/* The photo — or the initials circle behind it — carries the
                      author, so the name line no longer has to be the only thing
                      distinguishing one speaker from the next in a fast-moving
                      thread. Initials are the fallback, not a placeholder: most
                      of the company has never set a photo.

                      Within a run only the FIRST message shows it; the rest hold
                      the same width with an empty spacer so the bubbles below
                      stay on the one vertical edge. */}
                  {startsRun ? (
                    <Avatar
                      name={own ? t('cases.chat.you') : message.authorName}
                      src={own ? currentUserAvatarUrl : message.authorAvatarUrl}
                      size="sm"
                      className={cn('shrink-0', message.pending && 'opacity-65')}
                    />
                  ) : (
                    <div className="size-7 shrink-0" aria-hidden />
                  )}

                  <div
                    className={cn(
                      'flex min-w-0 flex-col gap-1',
                      // A bubble stops short of the panel edge. Full-width runs
                      // are what made the old thread read as stacked cards: with
                      // no ragged edge there was no shape to tell one speaker's
                      // column from the other's.
                      //
                      // The cap is a percentage AND a character measure. On the
                      // narrow drawer the percentage binds and keeps the ragged
                      // edge; once the panel is wide the ch cap takes over and
                      // stops a long paste from running to a line the eye loses
                      // its place tracking back from.
                      'max-w-[min(88%,52ch)]',
                      own && 'items-end',
                    )}
                  >
                    {startsRun ? (
                      <div
                        className={cn(
                          'flex items-baseline gap-1.5 px-0.5',
                          own && 'flex-row-reverse',
                        )}
                      >
                        <span className="text-text-body text-[12px] font-medium">
                          {own ? t('cases.chat.you') : message.authorName}
                        </span>
                        {/* RELATIVE, per UX-DR-13 — "4 min" rather than "24 Jul
                            16:20". The question asked of a coordination message
                            is "how stale is this instruction", and an absolute
                            clock time makes the reader do the arithmetic. The
                            absolute instant stays on the title so it is one hover
                            away, and past a week `relative` falls back to a date
                            on its own.

                            Both are computed in the READER's zone from a UTC
                            instant (AD-19): the Ashgabat and Yiwu offices are
                            three hours apart, and a server-composed age would
                            arrive in the wrong language as well as the wrong
                            zone (AD-27). */}
                        <span
                          className="text-text-faint text-[11px] font-light"
                          title={time.exact(message.sentAt)}
                        >
                          {time.relative(message.sentAt)}
                        </span>
                      </div>
                    ) : null}

                    {/* Asymmetric corners: the one corner nearest the speaker's
                        avatar is tightened, which is what gives a bubble a side
                        without drawing a tail. Mid-run bubbles tighten the top
                        corner too, so a run reads as one column of speech. */}
                    <div
                      className={cn(
                        'flex flex-col gap-1.5 text-[13px] leading-[1.45] font-normal',
                        'rounded-2xl px-3 py-2',
                        'transition-opacity duration-[var(--dur-fast)] ease-[var(--ease-standard)]',
                        own
                          ? 'bg-brand text-text-on-brand rounded-br-sm'
                          : 'bg-surface text-text-body border-line rounded-bl-sm border',
                        !startsRun && (own ? 'rounded-tr-sm' : 'rounded-tl-sm'),
                        message.pending && 'opacity-60',
                        // A photo fills its bubble edge to edge; the padding
                        // above is for text, and around an image it reads as a
                        // mount. Restored underneath when there is a caption too.
                        message.attachments?.length && !message.body && 'p-1.5',
                      )}
                    >
                      {message.attachments?.map((attachment) => (
                        <SentAttachment
                          key={attachment.id}
                          attachment={attachment}
                          own={own}
                        />
                      ))}
                      {/* A tombstone renders its NOTICE, not its body — which
                          the server has already cleared. Italic and muted so it
                          reads as a gap in the record rather than as something
                          somebody said (0029). */}
                      {message.deleted ? (
                        <span className="text-text-faint italic">
                          {t('chat.deletedBody')}
                        </span>
                      ) : message.body ? (
                        <span>{message.body}</span>
                      ) : null}
                    </div>

                    {/* The permanent edit marker. 0023 refused editing because a
                        SILENT rewrite is worse than no message; this is what
                        makes it never silent, so it is not conditional on
                        anything but the fact. */}
                    {message.edited && !message.deleted ? (
                      <span className="text-text-faint px-0.5 text-[11px]">
                        {t('chat.edited')}
                      </span>
                    ) : null}

                    {message.pending ? (
                      <span className="text-text-faint px-0.5 text-[11px]">
                        {t('cases.chat.sending')}
                      </span>
                    ) : null}
                  </div>
                </li>
              </Fragment>
            )
          })}
        </ul>
      </div>

      {/* The pill floats OVER the last line of the thread instead of taking a
          row of its own. As a laid-out element it pushed the whole
          conversation up by its own height the moment someone scrolled — the
          text moving under the reader was the opposite of what a
          jump-to-latest control is for. */}
      {/* The unread notice and the jump-to-latest control are ONE control, and
          that is the decision worth recording. They could have been a badge
          somewhere in the chrome plus a separate pill, but the two say the same
          thing — "something happened below you" — and the useful response to
          both is the same press. Two affordances for one act is how a reader
          ends up dismissing the badge and never finding the messages.

          So the pill states its own count when there is one and reads plainly
          "Latest" when the reader is merely scrolled up with nothing new. It is
          role="status" with aria-live so the arrival is announced rather than
          only drawn — the AC asks for an indicator "without scrolling or
          refreshing", and a screen reader user gets neither from a silent pill. */}
      {!atBottom ? (
        <div className="relative">
          <button
            type="button"
            onClick={jumpToLatest}
            aria-live="polite"
            className={cn(
              'absolute -top-11 left-1/2 z-10 -translate-x-1/2',
              'border-line flex items-center gap-1.5 rounded-full border py-1.5 pr-3.5 pl-3',
              'cursor-pointer text-[12px] font-medium shadow-[var(--shadow-overlay)]',
              'transition-colors duration-[var(--dur-fast)] ease-[var(--ease-standard)]',
              // Brand fill only when there is genuinely something new. A pill
              // that is always blue spends the accent on "you scrolled up",
              // which is not news.
              unread > 0
                ? 'bg-brand text-text-on-brand border-transparent hover:bg-blue-600'
                : 'bg-surface text-text-body hover:bg-surface-hover',
            )}
          >
            <ArrowDown
              size={13}
              aria-hidden
              className={unread > 0 ? '' : 'text-brand'}
            />
            {unread > 0
              ? t('cases.chat.unread', { count: unread })
              : t('cases.chat.latest')}
          </button>
        </div>
      ) : null}

      {/* The composer is ONE surface, not a field parked beside a button. The
          previous arrangement drew two competing rectangles — a bordered box
          and a square icon target — which read as two controls for a single
          act. Here the shell carries the border and the focus ring, the
          textarea inside it is chromeless, and the send key is a round brand
          disc riding in the same well. That is the shape every messenger this
          team already uses at home has, so the bar needs no learning.

          The ring lands on the shell via focus-within rather than on the
          textarea, so the whole control lights up as one thing; the global
          :focus-visible ring is suppressed on the textarea itself for the
          same reason. */}
      {/* The composer is ONE surface, not a field parked beside a button. The
          previous arrangement drew two competing rectangles — a bordered box
          and a square icon target — which read as two controls for a single
          act. Here the shell carries the border and the focus ring, the
          textarea inside it is chromeless, and the send key is a round brand
          disc riding in the same well. That is the shape every messenger this
          team already uses at home has, so the bar needs no learning.

          The bar itself has no top border. It sits on a blurred translucent
          ground so the thread appears to run underneath and fade out, which
          is what keeps a scrolling conversation feeling continuous instead of
          stopping dead at a rule. */}
      <form
        onSubmit={(event) => {
          event.preventDefault()
          submit()
        }}
        className="bg-surface/85 shrink-0 px-3 pt-2 pb-3 backdrop-blur-md"
      >
        <label htmlFor="chat-draft" className="sr-only">
          {t('cases.chat.messageLabel')}
        </label>

        {/* No `accept` filter. Operations sends whatever the situation
            produced — a CMR scan, a clip of a damaged pallet, a voice note
            from a driver — and an allowlist here would refuse the one format
            nobody anticipated. What a file is ALLOWED TO BECOME on screen is
            constrained at render instead (attachmentKind.ts), which is the
            place that decision is actually load-bearing. */}
        <input
          ref={pickerRef}
          type="file"
          multiple
          className="hidden"
          onChange={(event) => {
            addFiles(event.target.files)
            // Cleared so picking the same file twice in a row still fires a
            // change event; without this the second attempt is silent.
            event.target.value = ''
          }}
        />

        {rejected ? (
          <p
            role="status"
            className="text-negative mb-1.5 px-3.5 text-[11.5px] font-light"
          >
            {rejected}
          </p>
        ) : null}

        {/* Picked-but-unsent files. A row of removable chips rather than
            thumbnails: at composer size a 40px thumbnail of a CMR scan is
            indistinguishable from a 40px thumbnail of any other CMR scan,
            whereas the filename is what the sender is actually checking. */}
        {picked.length ? (
          <ul className="mb-2 flex flex-wrap gap-1.5 px-0.5">
            {picked.map((file, index) => (
              <li
                key={`${file.name}-${index}`}
                className="border-line bg-surface-sunken text-text-body flex max-w-[220px] items-center gap-1.5 rounded-full border py-1 pr-1 pl-2.5 text-[12px]"
              >
                <Paperclip size={12} aria-hidden className="text-text-muted shrink-0" />
                <span className="min-w-0 flex-1 truncate">{file.name}</span>
                <span className="text-text-faint shrink-0 text-[11px]">
                  {formatBytes(file.size)}
                </span>
                <button
                  type="button"
                  aria-label={t('cases.chat.removeAttachment', { name: file.name })}
                  onClick={() =>
                    setPicked((current) => current.filter((_, i) => i !== index))
                  }
                  className="text-text-muted hover:bg-surface-hover hover:text-text-heading inline-flex size-5 shrink-0 cursor-pointer items-center justify-center rounded-full transition-colors duration-[var(--dur-fast)]"
                >
                  <X size={12} aria-hidden />
                </button>
              </li>
            ))}
          </ul>
        ) : null}
        {/* The ring lands on the shell via focus-within rather than on the
            textarea, so the whole control lights up as one thing; the global
            :focus-visible ring is suppressed on the textarea itself for the
            same reason. rounded-[20px] rather than radius-full: a pill is
            correct at one line but bows out absurdly once the field grows to
            four, and this composer grows. */}
        <div
          className={cn(
            'border-line bg-surface-sunken flex items-end gap-1.5 rounded-[20px] border py-1.5 pr-1.5 pl-3.5',
            'transition-[border-color,background-color] duration-[var(--dur-fast)] ease-[var(--ease-standard)]',
            // Focus is carried by the border and the fill alone — no ring.
            // The border goes to full brand rather than the softer tint a ring
            // would have accompanied, so the focused state is still
            // unambiguous without a glow around the bar.
            'focus-within:border-brand focus-within:bg-surface',
          )}
        >
          {/* Leading rather than trailing: attaching happens BEFORE writing
              the caption, and it is the one control here that is not "send". */}
          {allowAttachments ? (
          <button
            type="button"
            aria-label={t('cases.chat.attach')}
            title={t('cases.chat.attach')}
            onClick={() => pickerRef.current?.click()}
            className={cn(
              'text-text-muted hover:bg-surface-hover hover:text-text-heading',
              '-ml-1.5 inline-flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full',
              'pointer-coarse:size-11',
              'transition-colors duration-[var(--dur-fast)] ease-[var(--ease-standard)]',
            )}
          >
            <Paperclip size={16} aria-hidden />
          </button>
          ) : null}

          <textarea
            ref={inputRef}
            id="chat-draft"
            rows={1}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              // Enter sends, Shift+Enter breaks the line — the convention every
              // user of this product already has from WhatsApp.
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault()
                submit()
              }
            }}
            onPaste={(event) => {
              // A screenshot on the clipboard is the fastest way a photo gets
              // into this thread, and Ctrl+V is where a user will try it first.
              // Only intercept when the clipboard actually carries files —
              // otherwise this would swallow ordinary text paste.
              const files = event.clipboardData?.files
              if (files?.length) {
                event.preventDefault()
                addFiles(files)
              }
            }}
            placeholder={t('cases.chat.placeholder')}
            className="text-text-body asa-scroll max-h-32 min-h-[26px] flex-1 resize-none border-0 bg-transparent py-1 font-sans text-[13px] leading-[1.45] font-normal outline-none focus:shadow-none focus-visible:shadow-none"
          />
          {/* Disabled on an empty draft rather than silently doing nothing when
              pressed: the button's own appearance is the answer to "why did
              that not send?". aria-disabled keeps it reachable so a screen
              reader user can find it and hear the state, instead of the
              control vanishing from the tab order.

              It also scales up as the draft becomes sendable — the one piece
              of motion in the bar, and it is spent confirming that what you
              typed registered. */}
          <button
            type="submit"
            aria-label={t('cases.chat.send')}
            title={t('cases.chat.send')}
            aria-disabled={!canSend}
            className={cn(
              'inline-flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full',
              'pointer-coarse:size-11',
              'transition-[background-color,color,transform] duration-[var(--dur-fast)] ease-[var(--ease-standard)]',
              canSend
                ? 'bg-brand text-text-on-brand hover:bg-blue-600 scale-100'
                : 'bg-surface-disabled text-text-disabled scale-90 cursor-not-allowed',
            )}
          >
            <Send size={15} aria-hidden className="translate-x-px" />
          </button>
        </div>
        {/* The send convention, stated once where it is needed rather than
            left to be discovered. It fades in only while a draft is live —
            standing advice under an empty box is noise. */}
        <p
          aria-hidden
          className={cn(
            'text-text-faint mt-1.5 px-3.5 text-[11px] font-light',
            'transition-opacity duration-[var(--dur-fast)] ease-[var(--ease-standard)]',
            canSend ? 'opacity-100' : 'opacity-0',
          )}
        >
          {t('cases.chat.sendHint')}
        </p>
      </form>
    </div>
  )
}
